import { Component, OnInit, Output, EventEmitter, Input } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Observable, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, startWith, takeUntil } from 'rxjs/operators';
import { DocumentsRepositoryService } from 'src/app/services/documents-repository.service';
import { ShareDataService } from 'src/app/services/share-data.service';
import { DocumentsTypeByPath, FolderStructure, GetDocumentTypesRequest, SupplierDetails } from 'src/app/shared/models/documents.model';
import { SubscribedNotification } from 'src/app/shared/models/notification.model';
import { User } from 'src/app/shared/models/user.model';

@Component({
  selector: 'app-add-edit-notification',
  templateUrl: './add-edit-notification.component.html',
  styleUrls: ['./add-edit-notification.component.scss']
})
export class AddEditNotificationComponent implements OnInit {
  @Input() usersInfo: User;
  @Input() notificationList: SubscribedNotification;
  notificationForm: UntypedFormGroup;
  addNotification: boolean = false;
  manageNotification: boolean = true;
  suppliersList: SupplierDetails[];
  docType = [{id: 0, value: 'All Document Types'},{id: 1, value: 'Select Specific Types'}];
  @Output() backClicked = new EventEmitter();
  @Output() isSubmitButtonAvailable = new EventEmitter();
  @Output() isSaveValid = new EventEmitter();
  @Output() addClickInfo = new EventEmitter();
  @Output() onCancelClick = new EventEmitter();
  @Output() onClickToBack = new EventEmitter();
  @Output() onAddLinkClick = new EventEmitter();
  @Output() onEditIconClick = new EventEmitter();
  /** 0 = not in the multi-supplier add flow, 1 = supplier select, 2 = per-supplier config */
  @Output() stepChanged = new EventEmitter<number>();
  @Output() hasSupplierSelection = new EventEmitter<boolean>();
  
  private unsubscribe: Subject<void> = new Subject<void>();
  folderStructure: FolderStructure;
  documentsTypeByPath: DocumentsTypeByPath;
  subscribedList = [];
  defaultChecked: number;
  isAllDocSelected: boolean;
  selectedSupplier: any;
  editForm: boolean;
  supplierValueSet: any;
  editFormDetails: any;
  isAddFormVisible: boolean;
  isEditFormVisible: boolean;
  disableBasedOnRole: boolean;
  filteredOptions: Observable<[]>;
  options: any;

  // ---- Multi-supplier add flow state ----
  addStep: number = 1;
  assignedSuppliers: SupplierDetails[] = [];
  filteredSuppliers: SupplierDetails[] = [];
  selectedSuppliers: SupplierDetails[] = [];
  allSuppliersChecked: boolean = false;
  supplierSearch: UntypedFormControl = new UntypedFormControl('');
  configForm: UntypedFormGroup;
  docOptionsByIndex: any[][] = [];
  allDocOptionsByIndex: any[][] = [];
  selectedDocsByIndex: any[][] = [];


  constructor(private fb: UntypedFormBuilder,
              private shareDataService: ShareDataService,
              private documentsService: DocumentsRepositoryService) { }

  ngOnInit(): void {
     this.defaultChecked = this.docType[0].id;
     this.notificationForms();
     this.configForm = this.fb.group({ configs: this.fb.array([]) });
     this.configForm.valueChanges
       .pipe(takeUntil(this.unsubscribe))
       .subscribe(() => this.emitConfigValidity());
     this.loadAssignedSuppliers();
     this.registerSupplierSearch();
  }

  private notificationForms(): void {
    this.notificationForm = this.fb.group({
      supplier: this.fb.control('', [Validators.required]),
      uploaded: this.fb.control({value: true, disabled: true},),
      inAppNotification: this.fb.control(true), //, [Validators.required]),
      emailNotification: this.fb.control(false),
      perDocument: this.fb.control(false),
      weeklyNotification: this.fb.control(false),
      documentType: this.fb.control(''),
      search:this.fb.control('')
    });
    this.documentType.valueChanges.subscribe((val) => {
        this.isAllDocSelected = val == 0 ? false : true;
        if(!this.isAllDocSelected){
          this.subscribedList=[];
        }
    });
    this.notificationForm.valueChanges.subscribe((res) => {
        this.isSaveValid.emit(this.notificationForm.status == 'INVALID' ? true :  this.notificationForm.dirty && this.isAllDocSelected && this.subscribedList.length===0 ? true : false);
    });
  };

  private _filter(value) {
    const filterValue = value.toLowerCase();
    let val;
    if(filterValue) {
     val = this.options.filter(option => option.documentTypeString.toLowerCase().includes(filterValue));
    }
    return val;
  }

  displayFn(user) {
   
  //  return user  ? user : '';
  }

  private getAllSuppliers(): void {
    this.shareDataService
      .getAllSuppliers()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(
        (response) => {
          this.suppliersList = response;
        },
        // (error) => {
        //   this.snackBarService.showError(error);
        // }
      );
  };

  // ---- Multi-supplier add flow ----
  private loadAssignedSuppliers(): void {
    const cwsId = this.usersInfo ? this.usersInfo.cwsId : null;
    if (!cwsId) { return; }
    this.shareDataService
      .getSuppliersByUserId(cwsId)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((response) => {
        this.assignedSuppliers = response || [];
        this.suppliersList = this.assignedSuppliers;
        this.refreshFilteredSuppliers();
      });
  }

  private registerSupplierSearch(): void {
    this.supplierSearch.valueChanges
      .pipe(debounceTime(200), distinctUntilChanged(), takeUntil(this.unsubscribe))
      .subscribe(() => this.refreshFilteredSuppliers());
  }

  private get subscribedSupplierIds(): string[] {
    return this.activeNotifications
      .map((n) => (n.supplierId + '').toUpperCase().trim());
  }

  get activeNotifications(): any[] {
    const list = (this.notificationList as any) || [];
    return list.filter(
      (n) => n && n.supplierId && (n.documentTypes || n.notificationType)
    );
  }

  private get availableSuppliers(): SupplierDetails[] {
    const taken = this.subscribedSupplierIds;
    return this.assignedSuppliers.filter(
      (s) => taken.indexOf(((s.id || '') + '').toUpperCase().trim()) === -1
    );
  }

  private refreshFilteredSuppliers(): void {
    const term = (this.supplierSearch.value || '').toString().toLowerCase().trim();
    const source = this.availableSuppliers;
    if (!term) {
      this.filteredSuppliers = source;
    } else {
      this.filteredSuppliers = source.filter((s) =>
        ((s.supplierName || '') + ' ' + (s.organizationCode || ''))
          .toLowerCase()
          .includes(term)
      );
    }
  }

  isSupplierSelected(supplier: SupplierDetails): boolean {
    return this.selectedSuppliers.some((s) => s.id === supplier.id);
  }

  toggleSupplier(supplier: SupplierDetails): void {
    if (this.isSupplierSelected(supplier)) {
      this.selectedSuppliers = this.selectedSuppliers.filter((s) => s.id !== supplier.id);
    } else {
      this.selectedSuppliers = [...this.selectedSuppliers, supplier];
    }
    this.allSuppliersChecked =
      this.availableSuppliers.length > 0 &&
      this.selectedSuppliers.length === this.availableSuppliers.length;
    this.hasSupplierSelection.emit(this.selectedSuppliers.length > 0);
  }

  toggleAllSuppliers(checked: boolean): void {
    this.allSuppliersChecked = checked;
    this.selectedSuppliers = checked ? [...this.availableSuppliers] : [];
    this.hasSupplierSelection.emit(this.selectedSuppliers.length > 0);
  }

  get configs(): UntypedFormArray {
    return this.configForm.get('configs') as UntypedFormArray;
  }

  goToConfigStep(): void {
    if (this.selectedSuppliers.length === 0) { return; }
    this.addStep = 2;
    this.configs.clear();
    this.docOptionsByIndex = [];
    this.allDocOptionsByIndex = [];
    this.selectedDocsByIndex = [];
    this.selectedSuppliers.forEach((supplier, index) => {
      this.configs.push(this.buildConfigGroup(supplier));
      this.selectedDocsByIndex[index] = [];
      this.docOptionsByIndex[index] = [];
      this.allDocOptionsByIndex[index] = [];
      this.loadConfigDocTypes(index, supplier.id);
      this.registerConfigDocSearch(index);
    });
    this.stepChanged.emit(2);
    this.emitConfigValidity();
  }

  backToSupplierStep(): void {
    this.addStep = 1;
    this.refreshFilteredSuppliers();
    this.stepChanged.emit(1);
    this.hasSupplierSelection.emit(this.selectedSuppliers.length > 0);
    this.isSaveValid.emit(true);
  }

  private buildConfigGroup(supplier: SupplierDetails): UntypedFormGroup {
    return this.fb.group({
      supplierId: this.fb.control(supplier.id),
      supplierName: this.fb.control(supplier.supplierName),
      organizationCode: this.fb.control(supplier.organizationCode),
      uploaded: this.fb.control({ value: true, disabled: true }),
      inAppNotification: this.fb.control(true),
      emailNotification: this.fb.control(false),
      perDocument: this.fb.control(false),
      documentType: this.fb.control(0),
      search: this.fb.control('')
    });
  }

  private loadConfigDocTypes(index: number, supplierId: string): void {
    const filePathData: GetDocumentTypesRequest = {
      folderPath: 'Main Folders',
      supplierId: supplierId
    };
    this.documentsService
      .getDocumentTypesByPath(filePathData)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((docTypesByPath: DocumentsTypeByPath) => {
        if (docTypesByPath) {
          this.allDocOptionsByIndex[index] = docTypesByPath.item2 || [];
          this.docOptionsByIndex[index] = docTypesByPath.item2 || [];
        }
      });
  }

  private registerConfigDocSearch(index: number): void {
    const group = this.configs.at(index) as UntypedFormGroup;
    group.get('search').valueChanges
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.unsubscribe))
      .subscribe((value) => {
        const source = this.allDocOptionsByIndex[index] || [];
        const term = (value || '').toString().toLowerCase();
        this.docOptionsByIndex[index] = term
          ? source.filter((x) => x.documentTypeString.toString().toLowerCase().indexOf(term) > -1)
          : source;
      });
  }

  onConfigEmailChange(index: number): void {
    const group = this.configs.at(index) as UntypedFormGroup;
    if (group.get('emailNotification').value === false) {
      group.get('perDocument').setValue(false);
    }
  }

  onConfigDocModeChange(index: number): void {
    const group = this.configs.at(index) as UntypedFormGroup;
    if (group.get('documentType').value === 0) {
      this.selectedDocsByIndex[index] = [];
    }
    this.emitConfigValidity();
  }

  addConfigDocType(index: number, selectedValue?: string): void {
    const group = this.configs.at(index) as UntypedFormGroup;
    const value = selectedValue !== undefined ? selectedValue : group.get('search').value;
    const source = this.allDocOptionsByIndex[index] || [];
    const docAvailable = source.some((el) => el.documentTypeString === value);
    if (!docAvailable) { return; }
    const docType = {
      documentType: value,
      upload: 0,
      download: 0,
      moved: 0,
      isRead: false
    };
    const current = this.selectedDocsByIndex[index] || [];
    current.push(docType);
    this.selectedDocsByIndex[index] = [
      ...new Map(current.map((m) => [m.documentType, m])).values()
    ];
    group.get('search').setValue('');
    this.emitConfigValidity();
  }

  removeConfigDocType(index: number, docIndex: number): void {
    this.selectedDocsByIndex[index].splice(docIndex, 1);
    this.emitConfigValidity();
  }

  private emitConfigValidity(): void {
    const valid = this.configs.length > 0 && this.configs.controls.every((group, index) => {
      const specific = group.get('documentType').value === 1;
      const hasDocTypes = !specific || (this.selectedDocsByIndex[index] || []).length > 0;
      const hasNotificationType =
        group.get('inAppNotification').value === true || group.get('emailNotification').value === true;
      return hasDocTypes && hasNotificationType;
    });
    this.isSaveValid.emit(!valid);
  }

  getAddPayload(userId: string): any {
    const subscribedNotification = this.configs.controls.map((group, index) => {
      const isSpecific = group.get('documentType').value === 1;
      return {
        supplierId: group.get('supplierId').value,
        supplierName: group.get('supplierName').value,
        organizationCode: group.get('organizationCode').value,
        notificationEvents: {
          isUpload: group.get('uploaded').value
        },
        documentTypes: {
          isAllDocumentType: !isSpecific,
          selectedDocumentTypes: isSpecific ? (this.selectedDocsByIndex[index] || []) : []
        },
        notificationType: {
          inAppNotification: group.get('inAppNotification').value === true,
          emailNotification: {
            perDocument: group.get('perDocument').value === true,
            weeklyNotification: false
          }
        },
        uploadCount: 0,
        downloadCount: 0,
        movedCount: 0
      };
    });
    return {
      id: '',
      userId: userId,
      subscribedToNotificationJson: {
        subscribedNotification: subscribedNotification
      },
      comments: ''
    };
  }

  // onChangeSupplier(id) {    
  //   this.documentsService
  //     .getDocumentsFolder(id)
  //     .pipe(takeUntil(this.unsubscribe))
  //     .subscribe(
  //       (response) => {
  //         this.folderStructure=response;
  //       })
  // }

  private getDocumentsType(filePathData: GetDocumentTypesRequest) {
    this.documentsService
      .getDocumentTypesByPath(filePathData)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(
        (docTypesByPath: DocumentsTypeByPath) => {
          if(docTypesByPath) {
          this.documentsTypeByPath = docTypesByPath;
          this.options = docTypesByPath.item2;
          this.onDocSearch(docTypesByPath.item2);
          }
        });
  }
  onDocSearch(item) {
    this.notificationForm.get('search').valueChanges
      .pipe(debounceTime(500), distinctUntilChanged()
      ).subscribe(value => {
        if (value.length > 0) {
          this.options = item.filter(
            (x) =>
              x.documentTypeString
                .toString()
                .toLowerCase()
                .indexOf(value.toLowerCase()) > -1
          );
        } else {
          this.options = item;
        }
      });
  }

  checkDocumentExist() { 
    if(this.options.length == 0) {
      this.search.setErrors({ doesNotExists: true });
      this.isSaveValid.emit(this.notificationForm.status == 'INVALID' ? true : this.editForm == true && !this.notificationForm.dirty ? true : false);
    }
  }

  onSupplierChange(event) {
    this.selectedSupplier = event.value;
    let filePathData: GetDocumentTypesRequest = {
      folderPath: 'Main Folders',
      supplierId: event.value.id
    }
   // this.onChangeSupplier(event.value);
    this.getDocumentsType(filePathData)
  }

  onAddClick(selectedValue?: string) {
    const docValue = selectedValue !== undefined ? selectedValue : this.search.value;
    if(!this.editForm) {
    let formfilesAndSupplierInfo = {
      selectedSupplier: this.selectedSupplier,
      subscribedList: this.subscribedList
    }
    let selectedDocTypes = {
      documentType: docValue,
      upload: 0,
      download: 0,
      moved: 0,
      isRead: false
    }
      let docAvailable = this.documentsTypeByPath.item2.some(el => el.documentTypeString == docValue);
      if (docAvailable) {
        this.subscribedList.push(selectedDocTypes);
        this.subscribedList = [...new Map(this.subscribedList.map((m) => [m.documentType, m])).values()];
        this.addClickInfo.emit(formfilesAndSupplierInfo);
        this.search.setValue('');
      } else {
        //this.search.setValue('');
      }
   } else {
    let formfilesAndSupplierInfo = {
      selectedSupplier: this.editFormDetails,
      subscribedList: this.subscribedList
    }
    let selectedDocTypes = {
      documentType: docValue,
      upload: 0,
      download: 0,
      moved: 0,
      isRead: false
    }
      let docAvailable = this.documentsTypeByPath.item2.some(el => el.documentTypeString == docValue);
      if (docAvailable) {
        this.subscribedList.push(selectedDocTypes);
        this.subscribedList = [...new Map(this.subscribedList.map((m) => [m.documentType, m])).values()];
        this.addClickInfo.emit(formfilesAndSupplierInfo);
        this.search.setValue('');
      } else {
       // this.search.setValue('');
      }
   }
  }

  onCrossClick(event) {
    if(!this.editForm) {
      this.onCancelClick.emit(event);
    } else {
      let formfilesAndSupplierInfo = {
        selectedSupplier: this.editFormDetails,
        subscribedList: this.subscribedList,
        event: event 
      }
      this.onCancelClick.emit(formfilesAndSupplierInfo);
    }
  }

  onEditClick(event, index) {
    this.editFormDetails = event;
    let filePathData: GetDocumentTypesRequest = {
      folderPath: 'Main Folders',
      supplierId: event.supplierId
    }
    this.addNotification = true;
    this.manageNotification = false;
    this.editForm = true;
    let formheader = {
      isAddFormVisible: false,
      isEditFormVisible: true,
      isDeleteButtonVisisble:event.documentTypes?true:false,
    }
    this.onEditIconClick.emit(formheader);
    this.stepChanged.emit(0);
    this.getDocumentsType(filePathData);
    this.setFormData(event);
  }

  setFormData(formInfo) {
    this.supplierValueSet = formInfo.supplierName;
    this.defaultChecked =  formInfo.documentTypes == null ? 0 : formInfo.documentTypes.isAllDocumentType == false  ? 1 : 0;
    this.isAllDocSelected =  this.defaultChecked == 0 ? false : true;
    this.notificationForm.patchValue({
      supplier: formInfo.supplierName,
      uploaded: formInfo.notificationEvents == null ? true : formInfo.notificationEvents.isUpload,
      inAppNotification: formInfo.notificationType == null ? false : formInfo.notificationType.inAppNotification,
      emailNotification: false,
      perDocument: formInfo.notificationType || formInfo.notificationType?.emailNotification.perDocument == null 
      ? false : formInfo.notificationType?.emailNotification?.perDocument,
      weeklyNotification: formInfo.notificationType || formInfo.notificationType?.emailNotification.weeklyNotification == null 
      ? false : formInfo.notificationType?.emailNotification?.weeklyNotification,
      documentType: formInfo.documentTypes == null ? [] : formInfo.documentTypes?.selectedDocumentTypes,
      search: ''
    });
    if (formInfo.notificationType) {
      if (formInfo.notificationType.emailNotification.perDocument == true) {
        this.notificationForm.controls.emailNotification.setValue(true);
        this.notificationForm.controls.perDocument.setValue(true);
      } 
      if(formInfo.notificationType.emailNotification.weeklyNotification == true) {
        this.notificationForm.controls.emailNotification.setValue(true);
        this.notificationForm.controls.weeklyNotification.setValue(true);
      }
    }
    this.notificationForm.controls.supplier.disable();
    this.isSubmitButtonAvailable.emit(this.addNotification);
    if (formInfo.documentTypes) {
      this.subscribedList = formInfo.documentTypes.selectedDocumentTypes;
    }
  }

  clickToBack(){
      let formheader = {
      isAddFormVisible: false,
      isEditFormVisible: false
      }
      this.onClickToBack.emit(formheader);
      this.backClicked.emit('back');
  }
  addManageNotification(){
    this.subscribedList = [];
    this.addNotification = true;
    this.manageNotification = false;
    this.editForm = false;
    this.addStep = 1;
    this.selectedSuppliers = [];
    this.allSuppliersChecked = false;
    this.supplierSearch.setValue('');
    this.configs.clear();
    this.docOptionsByIndex = [];
    this.allDocOptionsByIndex = [];
    this.selectedDocsByIndex = [];
    this.refreshFilteredSuppliers();
    let formheader = {
      isAddFormVisible: true,
      isEditFormVisible: false
    }
    this.onAddLinkClick.emit(formheader);
    this.isSubmitButtonAvailable.emit(this.addNotification);
    this.stepChanged.emit(1);
    this.hasSupplierSelection.emit(false);
    this.isSaveValid.emit(true);
  }
  backToNotificationManage(){
    this.addNotification = false;
    this.manageNotification = true;
    this.addStep = 1;
    this.selectedSuppliers = [];
    this.allSuppliersChecked = false;
    let formheader = {
      isAddFormVisible: false,
      isEditFormVisible: false
    }
    this.onClickToBack.emit(formheader);
    this.isSubmitButtonAvailable.emit(this.addNotification);
    this.stepChanged.emit(0);
  }
  emailCheckOnChange(){
    if(this.emailNotification.value==false)
    {
      this.perDocument.setValue(false);
      this.weeklyNotification.setValue(false);
    }
  }
    get search() {
    return this.notificationForm.get("search") as UntypedFormControl;
  }
  get documentType() {
    return this.notificationForm.get("documentType") as UntypedFormControl;
  }
  get weeklyNotification() {
    return this.notificationForm.get("weeklyNotification") as UntypedFormControl;
  }
  get perDocument() {
    return this.notificationForm.get("perDocument") as UntypedFormControl;
  }
  get emailNotification() {
    return this.notificationForm.get("emailNotification") as UntypedFormControl;
  }
  get inAppNotification() {
    return this.notificationForm.get("inAppNotification") as UntypedFormControl;
  }
  get uploaded() {
    return this.notificationForm.get("uploaded") as UntypedFormControl;
  }
  get supplier() {
    return this.notificationForm.get("supplier") as UntypedFormControl;
  }
}
